import { LegalShell, LegalSection, LegalList, SupportLink } from "../site/LegalShell.jsx";

export default function TermsPage() {
  return (
    <LegalShell
      eyebrow="Legal"
      title="Terms of service"
      intro="The agreement between you and Aloha Rates when you use this site to find and book a place to stay in Hawaiʻi."
      updated="6 September 2026"
    >
      <LegalSection heading="Agreeing to these terms">
        <p>
          By using aloharates.com you accept these terms. If you do not accept them, please do not use the site.
          If you are booking on behalf of other people, you confirm you are authorised to accept these terms for
          everyone on the reservation.
        </p>
      </LegalSection>

      <LegalSection heading="What Aloha Rates does">
        <p>
          We are an online travel agency. We show availability and rates for hotels, resorts, condos and vacation
          rentals in Hawaiʻi, and we take your reservation. The stay itself is provided by the property, not by us.
          Once a booking is confirmed, your accommodation contract is with that property, on its terms, and it is
          responsible for the room, the building and the service you receive.
        </p>
      </LegalSection>

      <LegalSection heading="Rates, taxes and fees">
        <p>
          We show the total price for your dates with taxes and known mandatory fees included, because a nightly
          rate that excludes Hawaiʻi's transient accommodations and general excise taxes is not a real price.
        </p>
        <p>
          Some charges can only be collected by the property on arrival — resort fees at certain hotels, parking,
          cleaning or damage deposits on rentals, and any incidentals you add during your stay. Where we know about
          such a charge we show it. Rates are live and change constantly; a price is only fixed once your booking
          is confirmed.
        </p>
      </LegalSection>

      <LegalSection heading="Making a booking">
        <p>
          You must be at least 18 and able to enter into a binding contract. Guest names must match the government
          photo ID that will be presented at check-in — properties do refuse entry over mismatched names. Please
          check every detail before you confirm, particularly dates and spelling.
        </p>
        <p>
          A booking is confirmed when we send you a confirmation containing a booking reference. Until then, nothing
          is held.
        </p>
      </LegalSection>

      <LegalSection heading="Payment">
        <p>
          Payment is taken through our payment provider at the time and in the manner shown at checkout. Some rates
          are charged in full when you book; others are settled with the property on arrival. Which one applies is
          stated before you pay.
        </p>
      </LegalSection>

      <LegalSection heading="Changes and cancellations">
        <p>
          Every rate carries the cancellation policy set by the property, and that policy governs. It is shown
          before you book and repeated on your confirmation. Non-refundable rates are exactly that: they are
          cheaper because the money is not coming back.
        </p>
        <p>
          You can request a change or cancellation on the{" "}
          <a href="/booking/" className="text-ocean-700 underline underline-offset-2 hover:text-ocean-900">manage booking</a>{" "}
          page or by emailing <SupportLink/>. Refunds, where the policy allows them, are returned to the original
          payment method and the timing depends on your bank.
        </p>
        <p>
          A property may occasionally cancel or move a reservation — overbooking, damage, closure. Where that
          happens we will tell you as soon as we know and help you find an alternative or obtain a refund.
        </p>
      </LegalSection>

      <LegalSection heading="Property information">
        <p>
          Descriptions, photographs, amenity lists, star ratings and review scores are supplied to us by the
          properties and by our accommodation supply partner. We work to keep them accurate and we pass corrections
          upstream, but we do not independently inspect thousands of properties and we do not warrant that every
          detail is current. If something is wrong, tell us at <SupportLink/> and we will chase it.
        </p>
      </LegalSection>

      <LegalSection heading="Errors">
        <p>
          Prices and availability come from live systems, and systems occasionally produce obvious mistakes.
          Where a rate is clearly erroneous we may cancel the affected booking and refund you in full rather than
          honour it. We will not use this to walk away from an ordinary price movement.
        </p>
      </LegalSection>

      <LegalSection heading="Using the site">
        <p>Please do not:</p>
        <LegalList items={[
          "scrape, harvest or bulk-copy rates, content or property data from the site",
          "make speculative, false or fraudulent reservations",
          "interfere with the site's operation or attempt to access systems or data you have no right to",
          "reproduce our text, design or photography without permission"
        ]}/>
        <p>
          The site's design, text and code are owned by Aloha Rates or licensed to us. Property photography belongs
          to the properties and their licensors.
        </p>
      </LegalSection>

      <LegalSection heading="Liability">
        <p>
          We are responsible for handling your booking with reasonable care and skill. We are not responsible for
          the acts or omissions of a property, for the quality of your stay, or for events outside our reasonable
          control such as weather, natural events, flight disruption or public health measures.
        </p>
        <p>
          To the extent the law allows, our total liability arising out of a booking is limited to the amount you
          paid us for it. Nothing in these terms limits liability for death or personal injury caused by our
          negligence, for fraud, or for anything else that cannot lawfully be limited. Travel insurance is strongly
          recommended.
        </p>
      </LegalSection>

      <LegalSection heading="Governing law">
        <p>
          These terms are governed by the laws of the State of Hawaiʻi, and the courts of Hawaiʻi have jurisdiction
          over any dispute, without affecting any consumer-protection rights you have where you live.
        </p>
      </LegalSection>

      <LegalSection heading="Changes to these terms">
        <p>
          We may update these terms. The version that applies to your booking is the one published when you made
          it. The “last updated” date at the top shows when this page changed.
        </p>
      </LegalSection>

      <LegalSection heading="Contact">
        <p>Questions about these terms go to <SupportLink/>.</p>
      </LegalSection>
    </LegalShell>
  );
}
